import React, { useState, useEffect } from 'react';
import { Building2, Users, Calendar, Settings, CheckCircle, AlertCircle, Eye, CreditCard as Edit, Trash2, Plus } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Tenant {
  id: string;
  name: string;
  subdomain: string;
  custom_domain?: string;
  plan: 'starter' | 'professional' | 'enterprise';
  status: 'active' | 'suspended' | 'trial' | 'cancelled';
  branding: {
    primaryColor: string;
    secondaryColor: string;
    hospitalName: string;
    logo?: string;
  };
  features: {
    aiPredictions: boolean;
    automatedReminders: boolean;
    analytics: boolean;
    apiAccess: boolean;
    customIntegrations: boolean;
    whiteLabeling: boolean;
  };
  limits: {
    maxUsers: number;
    maxAppointments: number;
    maxPredictions: number;
    dataRetentionDays: number;
  };
  created_at: string;
  updated_at: string;
}

export const TenantDashboard: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching tenants from database...');
      
      const { data, error: fetchError } = await supabase
        .from('tenants')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) {
        console.error('Supabase error fetching tenants:', fetchError);
        throw fetchError;
      }

      console.log('Fetched tenants:', data);
      setTenants(data || []);
    } catch (err: any) {
      console.error('Error fetching tenants:', err);
      setError(err.message || 'Failed to fetch tenants');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'trial': return 'bg-blue-100 text-blue-800';
      case 'suspended': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPlanColor = (plan: string) => {
    switch (plan) {
      case 'starter': return 'bg-gray-100 text-gray-800';
      case 'professional': return 'bg-blue-100 text-blue-800';
      case 'enterprise': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const deleteTenant = async (tenantId: string) => {
    if (!confirm('Are you sure you want to delete this tenant? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('tenants')
        .delete()
        .eq('id', tenantId);

      if (error) throw error;

      // Refresh the list
      await fetchTenants();
      alert('Tenant deleted successfully');
    } catch (err: any) {
      console.error('Error deleting tenant:', err);
      alert(`Failed to delete tenant: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Tenants</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={fetchTenants}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (selectedTenant) {
    return (
      <div className="p-8">
        <div className="mb-6">
          <button
            onClick={() => setSelectedTenant(null)}
            className="text-blue-600 hover:text-blue-800 mb-4"
          >
            ← Back to Tenant List
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Tenant Details</h1>
        </div>

        <div className="bg-white shadow rounded-lg">
          <div className="bg-white shadow">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between h-16">
                <div className="flex items-center space-x-3">
                  <Building2 className="w-8 h-8" style={{ color: selectedTenant.branding.primaryColor }} />
                  <div>
                    <h1 className="text-xl font-bold text-gray-900">
                      AttendAI - {selectedTenant.branding.hospitalName}
                    </h1>
                    <p className="text-sm text-gray-600">{selectedTenant.subdomain}.attendai.io</p>
                  </div>
                </div>
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedTenant.status)}`}>
                  {selectedTenant.status}
                </div>
              </div>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-lg border p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <Users className="w-6 h-6 text-blue-600" />
                  <h3 className="font-semibold text-gray-900">Plan & Limits</h3>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Plan:</span>
                    <span className="font-medium capitalize">{selectedTenant.plan}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Max Users:</span>
                    <span className="font-medium">{selectedTenant.limits.maxUsers}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Max Appointments:</span>
                    <span className="font-medium">{selectedTenant.limits.maxAppointments.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Max Predictions:</span>
                    <span className="font-medium">{selectedTenant.limits.maxPredictions.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <Settings className="w-6 h-6 text-green-600" />
                  <h3 className="font-semibold text-gray-900">Features</h3>
                </div>
                <div className="space-y-2">
                  {Object.entries(selectedTenant.features).map(([feature, enabled]) => (
                    <div key={feature} className="flex items-center space-x-2">
                      {enabled ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
                      )}
                      <span className={`text-sm ${enabled ? 'text-gray-900' : 'text-gray-500'}`}>
                        {feature.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-lg border p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <Calendar className="w-6 h-6 text-purple-600" />
                  <h3 className="font-semibold text-gray-900">Branding</h3>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm text-gray-600">Primary Color</label>
                    <div className="flex items-center space-x-2 mt-1">
                      <div 
                        className="w-6 h-6 rounded border"
                        style={{ backgroundColor: selectedTenant.branding.primaryColor }}
                      />
                      <span className="text-sm font-mono">{selectedTenant.branding.primaryColor}</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Secondary Color</label>
                    <div className="flex items-center space-x-2 mt-1">
                      <div 
                        className="w-6 h-6 rounded border"
                        style={{ backgroundColor: selectedTenant.branding.secondaryColor }}
                      />
                      <span className="text-sm font-mono">{selectedTenant.branding.secondaryColor}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Tenant Configuration</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Basic Information</h4>
                  <dl className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-gray-600">Tenant ID:</dt>
                      <dd className="font-mono text-gray-900">{selectedTenant.id}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-600">Subdomain:</dt>
                      <dd className="font-mono text-gray-900">{selectedTenant.subdomain}</dd>
                    </div>
                    {selectedTenant.custom_domain && (
                      <div className="flex justify-between">
                        <dt className="text-gray-600">Custom Domain:</dt>
                        <dd className="font-mono text-gray-900">{selectedTenant.custom_domain}</dd>
                      </div>
                    )}
                  </dl>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Access URLs</h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-gray-600">Primary URL:</span>
                      <a 
                        href={`https://${selectedTenant.subdomain}.attendai.io`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-2 text-blue-600 hover:text-blue-800 underline"
                      >
                        {selectedTenant.subdomain}.attendai.io
                      </a>
                    </div>
                    {selectedTenant.custom_domain && (
                      <div>
                        <span className="text-gray-600">Custom URL:</span>
                        <a 
                          href={`https://${selectedTenant.custom_domain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 text-blue-600 hover:text-blue-800 underline"
                        >
                          {selectedTenant.custom_domain}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Tenant Information</h1>
            <p className="text-gray-600 mt-2">Manage hospital organizations and their configurations</p>
          </div>
          <div className="text-sm text-gray-600">
            {tenants.length} tenant{tenants.length !== 1 ? 's' : ''} total
          </div>
        </div>
      </div>

      {tenants.length === 0 ? (
        <div className="text-center py-12">
          <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Tenants Found</h3>
          <p className="text-gray-600 mb-4">
            No hospital tenants have been created yet. Use the Tenant Setup to create your first tenant.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-4 font-medium text-gray-700">Hospital</th>
                  <th className="text-left p-4 font-medium text-gray-700">Domain</th>
                  <th className="text-left p-4 font-medium text-gray-700">Plan</th>
                  <th className="text-left p-4 font-medium text-gray-700">Status</th>
                  <th className="text-left p-4 font-medium text-gray-700">Created</th>
                  <th className="text-left p-4 font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tenants.map((tenant) => (
                  <tr key={tenant.id} className="hover:bg-gray-50">
                    <td className="p-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{tenant.name}</p>
                          <p className="text-sm text-gray-600">{tenant.branding.hospitalName}</p>
                        </div>
                      </div>
                    </td>
                    
                    <td className="p-4">
                      <div>
                        <p className="font-medium text-gray-900">{tenant.subdomain}.attendai.io</p>
                        {tenant.custom_domain && (
                          <p className="text-sm text-gray-600">{tenant.custom_domain}</p>
                        )}
                      </div>
                    </td>
                    
                    <td className="p-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPlanColor(tenant.plan)}`}>
                        {tenant.plan}
                      </span>
                    </td>
                    
                    <td className="p-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(tenant.status)}`}>
                        {tenant.status}
                      </span>
                    </td>
                    
                    <td className="p-4">
                      <p className="text-sm text-gray-900">
                        {new Date(tenant.created_at).toLocaleDateString()}
                      </p>
                    </td>
                    
                    <td className="p-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setSelectedTenant(tenant)}
                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => window.open(`https://${tenant.subdomain}.attendai.io`, '_blank')}
                          className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                          title="Visit Site"
                        >
                          <Building2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteTenant(tenant.id)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          title="Delete Tenant"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Total Hospitals</span>
              <span className="font-semibold text-gray-900">{tenants.length}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Active</span>
              <span className="font-semibold text-green-600">
                {tenants.filter(t => t.status === 'active').length}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Trial</span>
              <span className="font-semibold text-blue-600">
                {tenants.filter(t => t.status === 'trial').length}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Plan Distribution</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Starter</span>
              <span className="font-semibold text-gray-900">
                {tenants.filter(t => t.plan === 'starter').length}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Professional</span>
              <span className="font-semibold text-blue-600">
                {tenants.filter(t => t.plan === 'professional').length}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Enterprise</span>
              <span className="font-semibold text-purple-600">
                {tenants.filter(t => t.plan === 'enterprise').length}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-3">
            {tenants.slice(0, 3).map((tenant) => (
              <div key={tenant.id} className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{tenant.name}</p>
                  <p className="text-xs text-gray-600">
                    Created {new Date(tenant.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};